/**
 * Role-based permission system for BB Taxidermy
 *
 * Defines what each role can do across the application.
 * Used by usePermissions hook and Can component for frontend access control.
 */

/**
 * User roles in the system
 */
export type UserRole = 'super_admin' | 'admin' | 'manager' | 'staff'

/**
 * Actions that can be performed on resources
 */
export type ResourceAction =
  | 'view'
  | 'create'
  | 'edit'
  | 'delete'
  | 'export'
  | 'updateStage'
  | 'overrideTanneryPayment'
  | 'assignTaxidermist'
  | 'send'
  | 'updateStatus'
  | 'configure'

/**
 * Resource types in the system
 */
export type Resource =
  | 'users'
  | 'orders'
  | 'reports'
  | 'settings'
  | 'production'
  | 'inventory'
  | 'tannery'
  | 'notifications'
  | 'payments'
  | 'quickbooks'

/**
 * Permission string format: "resource.action"
 */
export type Permission = `${Resource}.${ResourceAction}`

/**
 * Permission set for a single resource
 */
export interface ResourcePermissions {
  view: boolean
  create?: boolean
  edit?: boolean
  delete?: boolean
  export?: boolean
  updateStage?: boolean
  overrideTanneryPayment?: boolean
  assignTaxidermist?: boolean
  send?: boolean
  updateStatus?: boolean
  configure?: boolean
}

/**
 * Complete permission set for a role
 */
export interface RolePermissionSet {
  users: ResourcePermissions
  orders: ResourcePermissions
  reports: ResourcePermissions
  settings: ResourcePermissions
  production: ResourcePermissions
  inventory: ResourcePermissions
  tannery: ResourcePermissions
  notifications: ResourcePermissions
  payments: ResourcePermissions
  quickbooks: ResourcePermissions
}

/**
 * Permission matrix for all roles
 *
 * - super_admin: Full built-in access to everything; ignores custom permission overrides
 * - admin: Full access to everything unless customized
 * - manager: Can manage orders/production, view reports, no user management
 * - staff: Order entry and production tracking only
 */
export const ROLE_PERMISSIONS: Record<UserRole, RolePermissionSet> = {
  super_admin: {
    users: {
      view: true,
      create: true,
      edit: true,
      delete: true,
    },
    orders: {
      view: true,
      create: true,
      edit: true,
      delete: true,
    },
    reports: {
      view: true,
      export: true,
    },
    settings: {
      view: true,
      edit: true,
    },
    production: {
      view: true,
      edit: true,
      updateStage: true,
      overrideTanneryPayment: true,
      assignTaxidermist: true,
    },
    inventory: {
      view: true,
      create: true,
      edit: true,
      delete: true,
    },
    tannery: {
      view: true,
      create: true,
      edit: true,
      delete: true,
    },
    notifications: {
      view: true,
      send: true,
      configure: true,
    },
    payments: {
      view: true,
      updateStatus: true,
    },
    quickbooks: {
      view: true,
      create: true,
      edit: true,
      delete: true,
      send: true,
      configure: true,
    },
  },

  admin: {
    users: {
      view: true,
      create: true,
      edit: true,
      delete: true,
    },
    orders: {
      view: true,
      create: true,
      edit: true,
      delete: true,
    },
    reports: {
      view: true,
      export: true,
    },
    settings: {
      view: true,
      edit: true,
    },
    production: {
      view: true,
      edit: true,
      updateStage: true,
      overrideTanneryPayment: true,
      assignTaxidermist: true,
    },
    inventory: {
      view: true,
      create: true,
      edit: true,
      delete: true,
    },
    tannery: {
      view: true,
      create: true,
      edit: true,
      delete: true,
    },
    notifications: {
      view: true,
      send: true,
      configure: true,
    },
    payments: {
      view: true,
      updateStatus: true,
    },
    quickbooks: {
      view: true,
      create: true,
      edit: true,
      delete: true,
      send: true,
      configure: true,
    },
  },

  manager: {
    users: {
      view: true,
      create: false,
      edit: false,
      delete: false,
    },
    orders: {
      view: true,
      create: true,
      edit: true,
      delete: false,
    },
    reports: {
      view: true,
      export: true,
    },
    settings: {
      view: false,
      edit: false,
    },
    production: {
      view: true,
      edit: true,
      updateStage: true,
      overrideTanneryPayment: false,
      assignTaxidermist: true,
    },
    inventory: {
      view: true,
      create: true,
      edit: true,
      delete: false,
    },
    tannery: {
      view: true,
      create: true,
      edit: true,
      delete: true,
    },
    notifications: {
      view: true,
      send: true,
      configure: false,
    },
    payments: {
      view: true,
      updateStatus: true,
    },
    quickbooks: {
      view: true,
      create: true,
      edit: true,
      delete: false,
      send: true,
      configure: false,
    },
  },

  staff: {
    users: {
      view: false,
      create: false,
      edit: false,
      delete: false,
    },
    orders: {
      view: true,
      create: true,
      edit: true,
      delete: false,
    },
    reports: {
      view: false,
      export: false,
    },
    settings: {
      view: false,
      edit: false,
    },
    production: {
      view: true,
      edit: false,
      updateStage: true,
      overrideTanneryPayment: false,
      assignTaxidermist: false,
    },
    inventory: {
      view: true,
      create: false,
      edit: false,
      delete: false,
    },
    tannery: {
      view: true,
      create: false,
      edit: false,
      delete: false,
    },
    notifications: {
      view: true,
      send: false,
      configure: false,
    },
    payments: {
      view: true,
      updateStatus: false,
    },
    quickbooks: {
      view: false,
      create: false,
      edit: false,
      delete: false,
      send: false,
      configure: false,
    },
  },
}

/**
 * Check if a role has a specific permission
 *
 * @param role - User role to check
 * @param permission - Permission in format "resource.action" (type-safe)
 * @returns true if role has the permission
 *
 * @example
 * hasPermission('admin', 'users.create') // true
 * hasPermission('staff', 'users.view') // false
 */
export function hasPermission(role: UserRole, permission: Permission): boolean {
  if (!permission || !permission.includes('.')) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[PERMISSIONS] Invalid permission format: "${permission}"`)
    }
    return false
  }

  const rolePerms = ROLE_PERMISSIONS[role]
  if (!rolePerms) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[PERMISSIONS] Unknown role: "${role}"`)
    }
    return false
  }

  const [resource, action] = permission.split('.') as [Resource, ResourceAction]

  const resourcePerms = rolePerms[resource]
  if (!resourcePerms) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[PERMISSIONS] Unknown resource in permission: "${permission}"`)
    }
    return false
  }

  if (!(action in resourcePerms)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[PERMISSIONS] Unknown action in permission: "${permission}"`)
    }
    return false
  }

  return resourcePerms[action] === true
}

/**
 * A single per-user permission override. `allowed: true` grants the perm,
 * `allowed: false` explicitly denies it (overriding the role default).
 */
export interface PermissionOverride {
  resource: Resource
  action: ResourceAction
  allowed: boolean
}

/**
 * Same as hasPermission, but applies a list of per-user overrides on top.
 * Lookup precedence: override -> role default.
 */
export function hasPermissionWithOverrides(
  role: UserRole,
  overrides: PermissionOverride[] | undefined,
  permission: Permission
): boolean {
  if (!permission || !permission.includes('.')) return false
  if (role === 'super_admin') return hasPermission(role, permission)
  const [resource, action] = permission.split('.') as [Resource, ResourceAction]

  if (overrides && overrides.length > 0) {
    const match = overrides.find((o) => o.resource === resource && o.action === action)
    if (match) return match.allowed
  }

  return hasPermission(role, permission)
}

/**
 * Like getPermissionsList, but folds in per-user overrides.
 */
export function getEffectivePermissionsList(
  role: UserRole,
  overrides: PermissionOverride[] | undefined
): Permission[] {
  if (role === 'super_admin') return getPermissionsList(role)

  const rolePerms = ROLE_PERMISSIONS[role]
  if (!rolePerms) return []

  const overrideMap = new Map<string, boolean>()
  if (overrides) {
    for (const o of overrides) overrideMap.set(`${o.resource}.${o.action}`, o.allowed)
  }

  const result: Permission[] = []
  for (const resource of RESOURCES) {
    const resourcePerms = rolePerms[resource]
    if (!resourcePerms) continue
    for (const action of ACTIONS) {
      const key = `${resource}.${action}` as Permission
      const override = overrideMap.get(key)
      const allowed = override !== undefined ? override : resourcePerms[action] === true
      if (allowed) result.push(key)
    }
  }
  return result
}

/**
 * Get all permissions for a role
 *
 * @param role - User role
 * @returns Copy of the role's permission set, or empty object if invalid
 */
export function getPermissionsForRole(role: UserRole): Partial<RolePermissionSet> {
  const perms = ROLE_PERMISSIONS[role]
  if (!perms) {
    return {}
  }

  // Return a deep copy to prevent mutation
  return JSON.parse(JSON.stringify(perms))
}

/**
 * Check if a role can access a resource (any action)
 *
 * @param role - User role
 * @param resource - Resource name
 * @returns true if role can perform any action on the resource
 */
export function canAccessResource(role: UserRole, resource: string): boolean {
  const rolePerms = ROLE_PERMISSIONS[role]
  if (!rolePerms) {
    return false
  }

  const resourcePerms = rolePerms[resource as Resource]
  if (!resourcePerms) {
    return false
  }

  // Check if any action is permitted
  return Object.values(resourcePerms).some((value) => value === true)
}

/**
 * Get list of resources a role can access
 *
 * @param role - User role
 * @returns Array of resource names the role can access
 */
export function getAccessibleResources(role: UserRole): Resource[] {
  const rolePerms = ROLE_PERMISSIONS[role]
  if (!rolePerms) {
    return []
  }

  return (Object.keys(rolePerms) as Resource[]).filter((resource) =>
    canAccessResource(role, resource)
  )
}

/**
 * Check if a role is admin
 */
export function isAdminRole(role: UserRole): boolean {
  return role === 'super_admin' || role === 'admin'
}

/**
 * Check if a role is manager or higher (manager, admin, super_admin)
 */
export function isManagerOrAboveRole(role: UserRole): boolean {
  return isAdminRole(role) || role === 'manager'
}

/**
 * Check if a role can manage other users
 */
export function canManageUsers(role: UserRole): boolean {
  return hasPermission(role, 'users.create') || hasPermission(role, 'users.edit')
}

/**
 * Get all permissions a role has as an array of permission strings
 *
 * @param role - User role
 * @returns Array of permission strings like ['users.view', 'orders.create', ...]
 */
export function getPermissionsList(role: UserRole): Permission[] {
  const rolePerms = ROLE_PERMISSIONS[role]
  if (!rolePerms) {
    return []
  }

  const permissions: Permission[] = []

  for (const [resource, actions] of Object.entries(rolePerms)) {
    for (const [action, allowed] of Object.entries(actions)) {
      if (allowed) {
        permissions.push(`${resource}.${action}` as Permission)
      }
    }
  }

  return permissions
}

/**
 * Compare two roles and return which has more permissions
 *
 * @returns 1 if roleA > roleB, -1 if roleA < roleB, 0 if equal
 */
export function compareRoles(roleA: UserRole, roleB: UserRole): number {
  const hierarchy: Record<UserRole, number> = {
    super_admin: 4,
    admin: 3,
    manager: 2,
    staff: 1,
  }

  return Math.sign((hierarchy[roleA] ?? 0) - (hierarchy[roleB] ?? 0))
}

/**
 * Default role for new users
 */
export const DEFAULT_ROLE: UserRole = 'staff'

/**
 * All available roles
 */
export const ALL_ROLES: UserRole[] = ['super_admin', 'admin', 'manager', 'staff']

/**
 * All resources in the system
 */
export const RESOURCES: Resource[] = [
  'users',
  'orders',
  'reports',
  'settings',
  'production',
  'inventory',
  'tannery',
  'notifications',
  'payments',
  'quickbooks',
]

/**
 * All actions that can be performed
 */
export const ACTIONS: ResourceAction[] = [
  'view',
  'create',
  'edit',
  'delete',
  'export',
  'updateStage',
  'overrideTanneryPayment',
  'assignTaxidermist',
  'send',
  'updateStatus',
  'configure',
]

/**
 * Human-readable labels for roles
 */
export const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Administrator',
  manager: 'Manager',
  staff: 'Staff',
}

/**
 * Role descriptions for UI
 */
export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  super_admin: 'Full built-in access to all features. Cannot be restricted by role permission toggles.',
  admin: 'Full access to all features including user management and settings',
  manager: 'Can manage orders, production, reports, and day-to-day QuickBooks invoicing. No integration configuration.',
  staff: 'Can create and manage orders, update production stages. Limited access.',
}
